import React, { useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import SidebarPlaceholder from 'tailwind-components/ContentWithSidebar/SidebarPlaceholder';
import SidebarContent from 'tailwind-components/ContentWithSidebar/SidebarContent';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import withLocalLoader from 'components/HOCs/withLocalLoader';
import { getAppointmentsViewData } from 'state/actions/actions';
import { getBonus } from 'state/actions/bonus';
import {
  APPOINTED_CARRIERS_TAB_ID,
  getCarrierParentGroupTabIds
} from 'helpers/carriers/carriers';
import useTabUrlState from 'hooks/useTabUrlState';
import { ALL_CARRIERS_TAB_ID } from './constants';
import CommissionsStickyHeader from './CommissionsStickyHeader';
import CommissionsCarrierSection from './CommissionsCarrierSection';
import BonusSidebar from './BonusSidebar';

function BonusView({ localLoader, setLocalLoader }) {
  const dispatch = useDispatch();
  const bonus = useSelector(state => state.bonus.bonus);
  const { appointedCarriers, carriersArray } = useSelector(
    state => state.appointments
  );
  const [bonusLoader, setBonusLoader] = useState({
    isActive: false,
    isHidden: null
  });
  const [activeCarrier, setActiveCarrier] = useState('');
  const carrierTabIds = useMemo(
    () => [
      APPOINTED_CARRIERS_TAB_ID,
      ...getCarrierParentGroupTabIds(carriersArray),
      ALL_CARRIERS_TAB_ID
    ],
    [carriersArray]
  );
  const [currentTab, setCurrentTab] = useTabUrlState({
    param: 'tab',
    tabIds: carrierTabIds,
    defaultTabId: ALL_CARRIERS_TAB_ID
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const cancelTokenSource = axios.CancelToken.source();
  const isBelowLg = useBelowBreakpoint('lg');

  useEffect(() => {
    dispatch(getAppointmentsViewData(setLocalLoader, cancelTokenSource, true));
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  const handleResetActiveCarrier = () => {
    setActiveCarrier('');
    setSidebarOpen(false);
    setBonusLoader({ isActive: false, isHidden: null });
  };

  const handleCarrierClick = carrierName => {
    if (bonusLoader.isActive) return;

    dispatch(getBonus(carrierName, setBonusLoader));
    setActiveCarrier(carrierName);
    setSidebarOpen(true);
  };

  const mainContent = (
    <SimpleContainer className='relative h-full min-h-0 gap-4'>
      <CommissionsStickyHeader
        title='Commission Bonuses'
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        handleResetActiveCarrier={handleResetActiveCarrier}
      />
      <CommissionsCarrierSection
        currentTab={currentTab}
        activeCarrierName={activeCarrier}
        onCarrierClick={handleCarrierClick}
        localLoader={localLoader}
        appointedCarriers={appointedCarriers}
      />
    </SimpleContainer>
  );

  const sidebarContent = activeCarrier ? (
    <SidebarContent includesHeader>
      <BonusSidebar
        bonusItems={bonus[activeCarrier]}
        bonusLoader={bonusLoader}
      />
    </SidebarContent>
  ) : (
    <SidebarPlaceholder text='Select a carrier to view bonus breakdown' />
  );

  return (
    <div className='h-full w-full'>
      <ContentWithSidebar
        mainContent={mainContent}
        sidebarContent={sidebarContent}
        sidebarAsSheetOnMobile
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={open => {
          if (!open) {
            if (isBelowLg) setSidebarOpen(false);
            else handleResetActiveCarrier();
          }
        }}
        sidebarTitle={activeCarrier ? `${activeCarrier}: Bonus Breakdown` : ''}
        handleCloseSidebar={() => {
          if (isBelowLg) {
            setSidebarOpen(false);
            return;
          }
          handleResetActiveCarrier();
        }}
        desktopHeaderClassName='bg-white rounded-t-xl'
        desktopSidebarGridClassName='lg:grid-cols-[minmax(0,1fr)_400px]'
      />
    </div>
  );
}

BonusView.propTypes = {
  localLoader: propTypes.bool,
  setLocalLoader: propTypes.func
};

export default withLocalLoader(BonusView, true, 'Commissions', 'Bonuses');
